import { withFormik } from 'formik';
import { compose, lifecycle, withHandlers, withProps } from 'recompose';

const state = {
  Draft: () => true,
  Published: props => (props.dirty && props.isValid) || true,
};

const hasNoErrors = errors =>
  Object.keys(errors).length === 0 && errors.constructor === Object;

export const hasPublishAttempted = status =>
  !!(status && status.publishAttempted);

export const getHasAttemptedSubmit = ({ submitCount, status }) =>
  submitCount > 0 || hasPublishAttempted(status);

export const getPublishValidationErrors = (validate, values, status) => {
  if (!hasPublishAttempted(status) || typeof validate !== 'function') {
    return {};
  }

  return validate({
    ...values,
    status: 'Published',
  });
};

const isEqual = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

const touchErrorFields = (touched, errors) =>
  Object.keys(errors).reduce(
    (result, field) => ({
      ...result,
      [field]: true,
    }),
    touched || {}
  );

export default opts =>
  compose(
    withFormik({
      handleSubmit: (data, { props, ...form }) => props.onSubmit(data, form),
      ...opts,
    }),
    withHandlers({
      handleChangeStatus: props => async status => {
        const values = {
          ...props.values,
          status,
        };
        const errors = await props.validateForm(values);
        const publishAttempted = status === 'Published';
        if (!hasNoErrors(errors)) {
          props.setStatus({
            ...props.status,
            publishAttempted,
            publishValidationErrors: publishAttempted ? errors : {},
          });
          props.setTouched(touchErrorFields(props.touched, errors));
          props.setErrors(errors);
        }

        if (state[status] && state[status](props) && hasNoErrors(errors)) {
          props.setFieldValue('status', status, false);
          props.setStatus({
            ...props.status,
            changingPublishStatus: true,
            publishAttempted: false,
            publishValidationErrors: {},
          });

          if (props.onSubmit) {
            props.setSubmitting(true);
            props.onSubmit(values, props);
          } else {
            props.submitForm();
          }
        }
      },
      handleDelete: ({ api, onDeleted = () => {} }) => {
        if (!api || !api._delete) return null;

        return id => {
          api._delete(id);
          onDeleted();
        };
      },
    }),
    lifecycle({
      componentDidUpdate(prevProps) {
        const publishAttempted = hasPublishAttempted(this.props.status);
        if (!publishAttempted) {
          return;
        }

        const shouldSync =
          !hasPublishAttempted(prevProps.status) ||
          prevProps.values !== this.props.values;

        if (!shouldSync) {
          return;
        }

        const errors = getPublishValidationErrors(
          opts.validate,
          this.props.values,
          this.props.status
        );
        const touched = touchErrorFields(this.props.touched, errors);
        const statusPublishErrors =
          this.props.status && this.props.status.publishValidationErrors;

        if (!isEqual(this.props.errors, errors)) {
          this.props.setErrors(errors);
        }

        if (!isEqual(this.props.touched, touched)) {
          this.props.setTouched(touched);
        }

        if (!isEqual(statusPublishErrors, errors)) {
          this.props.setStatus({
            ...this.props.status,
            publishValidationErrors: errors,
          });
        }
      },
    }),
    withProps(({ submitCount, status, values }) => ({
      hasAttemptedSubmit: getHasAttemptedSubmit({ submitCount, status }),
      publishValidationErrors: getPublishValidationErrors(
        opts.validate,
        values,
        status
      ),
    }))
  );
